import { pipe } from 'fp-ts/lib/function';
import { ColorType } from 'highcharts';
import Branding from 'domain/entity/branding';
import { WidgetType } from 'domain/entity/widget';
import {
    ChartAxis,
    ChartSerie,
    ChartSerieMetaBorder,
    ChartSeriesType,
    ChartValue,
    NumericChartValue,
    SerieBorder,
    SerieMetaStep,
} from 'domain/entity/widgets/ChartData';
import { getColor } from 'adapter/in/common/utils/widgetDataUtils';
import { ChartSeriesOptionsType, GradientColorSerie, HighChartsSerieDatetimeValue } from 'adapter/in/common/components/widgets/Chart/types';
import { getPatternSeries, isComparison } from 'adapter/out/client/widgets/data/mappers/ChartMapper';

export const TURBO_THRESHOLD = 3000;

const getTypeSerie = (serie: ChartSerie): ChartSeriesType =>
    serie.type === ChartSeriesType.LINE && serie.meta.area === true ? ChartSeriesType.AREA : transformChartSeriesType(serie.type);

const transformChartSeriesType = (type: ChartSeriesType) => {
    switch (type) {
        case ChartSeriesType.SCATTER:
            return ChartSeriesType.SCATTER;
        case ChartSeriesType.CHART_LIMIT:
        case ChartSeriesType.TOOLTIP:
        case ChartSeriesType.GROUPED_LINE:
            return ChartSeriesType.LINE;
        case ChartSeriesType.GROUPED_BAR:
            return ChartSeriesType.COLUMN;
        default:
            return type;
    }
};

const getGroupedTypeSerie = (type: ChartSeriesType) => (type === ChartSeriesType.LINE ? ChartSeriesType.SCATTER : ChartSeriesType.COLUMN);

export const parseSerie = (
    widgetType: WidgetType,
    branding: Branding,
    isLoading: boolean,
    axesY: ChartAxis[],
    firstSerieOfEachStackIndex: Set<number>
) => (serie: ChartSerie, index: number): ChartSeriesOptionsType => {
    const { data, color, rawColor } = generateColorAndData(serie, branding);
    const isGrouped = serie.type === ChartSeriesType.GROUPED_BAR || serie.type === ChartSeriesType.GROUPED_LINE;
    return pipe(getTypeSerie(serie), (type) =>
        pipe(isComparison(serie.axisX, serie.stack), (comparison) =>
            pipe(
                comparison && (serie.type === ChartSeriesType.COLUMN || serie.type === ChartSeriesType.GROUPED_BAR),
                (isComparisonColumn) => ({
                    borderColor: isComparisonColumn && isGrouped ? undefined : rawColor,
                    borderRadiusTopLeft: getRadius(index, widgetType, firstSerieOfEachStackIndex),
                    borderRadiusTopRight: getRadius(index, widgetType, firstSerieOfEachStackIndex),
                    borderWidth: isComparisonColumn ? 2 : 0,
                    color,
                    comparison,
                    dashStyle: isComparisonColumn ? ChartSerieMetaBorder.SOLID : getDashStyle(serie.fillPattern),
                    data,
                    name: serie.title,
                    stack: serie.stack,
                    stacking: serie.stack !== null && serie.stack !== undefined ? 'normal' : undefined,
                    threshold: 0,
                    tooltip: {
                        valueSuffix: isLoading ? undefined : ` ${axesY.find((axis) => axis.key === serie.axisY)?.units}`,
                    },
                    yAxis: axesY.findIndex((axis) => axis.key === serie.axisY),
                    type: isGrouped ? getGroupedTypeSerie(type) : type,
                    turboThreshold: TURBO_THRESHOLD,
                    enableMouseTracking: !serie.pointLess,
                    step: serie.meta.step && serie.meta.step !== SerieMetaStep.NONE ? serie.meta.step : undefined,
                    ...(type === ChartSeriesType.AREA && serie.meta ? { ...serie.meta } : {}),
                    ...(type === ChartSeriesType.SCATTER && {
                        marker: {
                            symbol: serie.fillPattern === SerieBorder.DASHED ? 'cross' : 'circle',
                            lineColor: null,
                            lineWidth: 2,
                            enabled: true,
                        },
                    }),
                    ...(serie.type === ChartSeriesType.GROUPED_LINE
                        ? {
                              lineWidth: getTypeSerie(serie) === ChartSeriesType.LINE ? 2 : undefined,
                          }
                        : {}),
                    ...(serie.type === ChartSeriesType.TOOLTIP
                        ? {
                              showInLegend: false,
                              yAxis: axesY.findIndex((axis) => axis.key === ChartSeriesType.TOOLTIP),
                          }
                        : {}),
                })
            )
        )
    );
};

export const BAR_BORDER_RADIUS = '4px';
const getRadius = (index: number, widgetType: WidgetType, firstSerieOfEachStackIndex: Set<number>): string => {
    if (firstSerieOfEachStackIndex.has(index)) {
        return BAR_BORDER_RADIUS;
    }

    switch (widgetType) {
        case WidgetType.BAR:
            return BAR_BORDER_RADIUS;
        case WidgetType.STACKED_BAR:
            if (index === 0) {
                return BAR_BORDER_RADIUS;
            }
            return '0px';
        default:
            return '0px';
    }
};

export const getDashStyle = (fillPattern: SerieBorder) => {
    switch (fillPattern) {
        case SerieBorder.DASHED:
            return ChartSerieMetaBorder.DASH;
        case SerieBorder.DOTTED:
            return ChartSerieMetaBorder.DOT;
        case SerieBorder.SOLID:
        default:
            return ChartSerieMetaBorder.SOLID;
    }
};

const generateColorAndData = (serie: ChartSerie, branding: Branding) => {
    const color = getColor(branding)(serie.color);
    switch (serie.type) {
        case ChartSeriesType.GROUPED_BAR:
        case ChartSeriesType.GROUPED_LINE: {
            return { ...generateColorAndDataGrouped(serie), rawColor: color };
        }
        default: {
            const patternOrColor = getPatternSeries(getColor(branding)(serie.color), serie.fillPattern, serie.type);
            return {
                data:
                    serie.values.length >= TURBO_THRESHOLD
                        ? serie.values.map((value) => [typeof value.x === 'number' ? value.x : value.x.getTime(), value.y])
                        : serie.values.map((value) => ({
                              x: typeof value.x === 'number' ? value.x : value.x.getTime(),
                              y: value.y,
                              t: (value as NumericChartValue)?.t ?? undefined,
                              unmodifiedX: value.unmodifiedX,
                              color: getPatternSeries(
                                  serie.type === ChartSeriesType.TOOLTIP && serie.meta.color
                                      ? serie.meta.color
                                      : getColor(branding)(serie.color),
                                  serie.fillPattern,
                                  serie.type
                              ),
                          })),
                color: patternOrColor,
                rawColor: color,
            };
        }
    }
};

const generateColorAndDataGrouped = (
    serie: ChartSerie
): { data: HighChartsSerieDatetimeValue[]; color: ColorType | GradientColorSerie } => {
    const trimValues = (values: ChartValue[]): ChartValue[] => {
        let startIndex = 0;
        let endIndex = values.length - 1;

        for (let i = 0; i < values.length; i += 1) {
            if (values[i].y === null) {
                startIndex += 1;
            } else {
                break;
            }
        }

        for (let i = values.length - 1; i >= 0; i -= 1) {
            if (values[i].y === null) {
                endIndex -= 1;
            } else {
                break;
            }
        }

        return values.slice(startIndex, endIndex + 1);
    };
    const colorStops: [number, string][] = [];
    const metaGroups = serie.meta.groups;
    const metaGroupColorsMap = new Map<string, string>();
    serie.meta.groups?.forEach((metaGroup) => metaGroupColorsMap.set(metaGroup.key, metaGroup.color));
    if (getTypeSerie(serie) === ChartSeriesType.LINE) {
        const trimmedSerieValues = trimValues(serie.values);
        if (trimmedSerieValues.length > 0) {
            trimmedSerieValues.forEach((value, i) => {
                const color = (value.group && metaGroupColorsMap.get(value.group)) ?? '#00000000';
                if (trimmedSerieValues.length > 1) {
                    colorStops.push([i / (trimmedSerieValues.length - 1), color]);
                } else if (trimmedSerieValues.length === 1) {
                    colorStops.push([0, color]);
                }
            });
        }
    }

    const data = serie.values.map((value: ChartValue) => ({
        x: typeof value.x === 'number' ? value.x : value.x.getTime(),
        y: value.y,
        unmodifiedX: value.unmodifiedX,
        borderColor: serie.type === ChartSeriesType.GROUPED_BAR ? value.group && metaGroupColorsMap.get(value.group) : undefined,
        color:
            getTypeSerie(serie) === ChartSeriesType.COLUMN
                ? pipe(value.group && metaGroupColorsMap.get(value.group), (color) =>
                      color ? getPatternSeries(color, serie.fillPattern, serie.type) : color
                  )
                : value.group && metaGroupColorsMap.get(value.group),
    }));

    return {
        data,
        color: {
            stops:
                getTypeSerie(serie) === ChartSeriesType.COLUMN && metaGroups
                    ? metaGroups.map((group, i) => [metaGroups.length === 1 ? 0 : i / (metaGroups.length - 1), group.color])
                    : colorStops,
            linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
        },
    };
};
